<!--
  UsageTiles.vue
  Callbot monthly settlement - the five total tiles above the hospital usage list
  (screen spec 1 "usage tiles", reworked to the approved 2026-10-27 Main mockup).

  Request: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 1", "요청 계약 1" (`hospital_totals`)
  - Hospital totals only (internal/test excluded). Shows `hospital_totals` as is; chips and search do not change it.
    The first tile's sub line ("내부·테스트 n곳 제외") carries the "excludes internal/test" meaning.
  - Tiles: 이용 병원 (internal n excluded) · 통화 (web tests n kept apart) · AI 응대 시간 (calls not ended n) ·
    상담사 연결 응답 / 요청 (answer rate) · 상담사 통화 시간 (average per answered call).
    One short sub line per tile; everything else goes to the tooltip:
    - 통화: outbound / dropped-before-AI (callsSubLabel). For months before the first auto close
      (isWebCountPending) also the "web count is final after close" hint (reply table 7 - web counts are read
      from vox and fill in only at close or after [vox 건수 확인] on the detail recon tab).
    - 상담사 연결: PBX n · vox direct n (`connect_mode`).
    - Duration tiles show hours + minutes (minutes + seconds below one hour); the exact value is in `title`.
  - Non-numeric values (e.g. not aggregated yet `"…"`) render as '—'.
  - props: totals (`hospital_totals` | null) · loading (first load / month change - labels stay, values skeleton) ·
    month ('YYYY-MM' - region label, web pending hint) · internalCount (internal/test companies, null when unknown)
-->
<template>
  <section class="usage-tiles" :aria-label="sectionLabel" :aria-busy="loading ? 'true' : 'false'">
    <dl class="usage-tiles__grid">
      <div v-for="tile in tiles" :key="tile.key" class="usage-tiles__tile">
        <dt class="usage-tiles__label">
          {{ tile.label }}
          <a-tooltip v-if="tile.tips.length && !loading">
            <template #title>
              <div v-for="(line, i) in tile.tips" :key="i">{{ line }}</div>
            </template>
            <InfoCircleOutlined class="usage-tiles__help" tabindex="0" role="img" :aria-label="tile.tips.join(' ')" />
          </a-tooltip>
        </dt>
        <dd v-if="loading" class="usage-tiles__value">
          <a-skeleton-input active size="small" class="usage-tiles__skeleton" />
        </dd>
        <template v-else>
          <dd class="usage-tiles__value" :class="{ 'is-empty': tile.empty }" :title="tile.full || undefined">
            <span v-for="(part, i) in tile.parts" :key="i" :class="{ 'usage-tiles__unit': part.unit }">{{
              part.text
            }}</span>
            <span v-if="tile.rest" class="usage-tiles__rest">{{ tile.rest }}</span>
          </dd>
          <dd v-if="tile.sub" class="usage-tiles__sub">{{ tile.sub }}</dd>
        </template>
      </div>
    </dl>
  </section>
</template>

<script setup>
import { InfoCircleOutlined } from '@ant-design/icons-vue';
import { computed } from 'vue';

import {
  EMPTY,
  formatCount,
  formatDuration,
  formatNumber,
  isNumber,
  isValidMonth,
  monthLabel,
} from '../service/settlementFormat';
import { WEB_PENDING_HINT, WEB_PENDING_TOOLTIP, callsSubLabel, isWebCountPending } from '../service/settlementRules';

const props = defineProps({
  /** Contract 1 `hospital_totals` - null before load / on failure (every value is '—') */
  totals: { type: Object, default: null },
  /** Draw skeletons in the value slots */
  loading: { type: Boolean, default: false },
  /** 'YYYY-MM' - region label and the web pending hint */
  month: { type: String, default: '' },
  /** Internal/test companies in the list rows - first tile sub line. null when unknown */
  internalCount: { type: Number, default: null },
});

const NOT_FINAL_TIP = '아직 끝나지 않은 통화가 있어 사용량이 덜 찼습니다. 통화가 끝나면 더해집니다.';

/** Hours + minutes from one hour up (seconds go to the title), minutes + seconds below */
function compactDuration(sec) {
  if (!isNumber(sec)) return EMPTY;
  const total = Math.max(0, Math.round(sec));
  const h = Math.floor(total / 3600);
  if (h === 0) return formatDuration(total);
  return `${formatNumber(h)}시간 ${Math.floor((total % 3600) / 60)}분`;
}

/** '25시간 47분' -> number and unit pieces (units render smaller) */
function splitUnits(text) {
  return String(text)
    .split(/(\d[\d,.]*)/)
    .map((s) => s.trim())
    .filter((s) => s !== '')
    .map((s) => ({ text: s, unit: !/^\d/.test(s) }));
}

/** Answer rate - '' when requests are 0 or not numbers */
function answerRate(answered, handoffs) {
  if (!isNumber(answered) || !isNumber(handoffs) || handoffs <= 0) return '';
  return `응답률 ${Math.round((answered / handoffs) * 100)}%`;
}

const tiles = computed(() => {
  const t = props.totals ?? {};
  const webPending = isWebCountPending(props.month);
  const internal = props.internalCount;
  const pbx = t.connect_mode?.pbx;
  const vox = t.connect_mode?.vox_direct;
  const notFinal = isNumber(t.not_final) && t.not_final > 0 ? t.not_final : 0;
  const talkSec = t.counselor_talk_seconds;

  const callsTip = [callsSubLabel(t), webPending ? `${WEB_PENDING_HINT}: ${WEB_PENDING_TOOLTIP}` : ''];

  const list = [
    {
      key: 'companies',
      label: '이용 병원',
      value: formatCount(t.companies, '곳'),
      sub: isNumber(internal) && internal > 0 ? `내부·테스트 ${formatNumber(internal)}곳 제외` : '내부·테스트 제외',
    },
    {
      key: 'calls',
      label: '통화',
      value: formatCount(t.calls, '통'),
      sub: isNumber(t.web) ? `웹 테스트 ${formatNumber(t.web)}건은 따로` : '',
      tip: callsTip,
    },
    {
      key: 'ai',
      label: 'AI 응대 시간',
      value: compactDuration(t.ai_seconds),
      full: isNumber(t.ai_seconds) ? formatDuration(t.ai_seconds) : '',
      sub: notFinal ? `안 끝난 통화 ${formatNumber(notFinal)}건` : '',
      tip: notFinal ? NOT_FINAL_TIP : '',
    },
    {
      key: 'handoff',
      label: '상담사 연결 응답',
      value: formatNumber(t.answered),
      rest: isNumber(t.handoffs) ? `/ ${formatNumber(t.handoffs)} 요청` : '',
      sub: answerRate(t.answered, t.handoffs),
      tip: isNumber(pbx) || isNumber(vox) ? `PBX ${formatNumber(pbx)} · vox 직접 ${formatNumber(vox)}` : '',
    },
    {
      key: 'talk',
      label: '상담사 통화 시간',
      value: compactDuration(talkSec),
      full: isNumber(talkSec) ? formatDuration(talkSec) : '',
      sub:
        isNumber(talkSec) && isNumber(t.answered) && t.answered > 0
          ? `응답 1통 평균 ${formatDuration(talkSec / t.answered)}`
          : '',
    },
  ];

  return list.map((tile) => ({
    ...tile,
    tips: (Array.isArray(tile.tip) ? tile.tip : [tile.tip]).filter(Boolean),
    empty: tile.value === EMPTY,
    parts: tile.value === EMPTY ? [{ text: EMPTY, unit: false }] : splitUnits(tile.value),
  }));
});

const sectionLabel = computed(() =>
  isValidMonth(props.month)
    ? `${monthLabel(props.month)} 병원 사용량 합계(내부·테스트 제외)`
    : '병원 사용량 합계(내부·테스트 제외)'
);
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

.usage-tiles {
  // Column count follows this region's width, not the viewport (5 / 3 / 2 with or without the sidebar)
  container-type: inline-size;

  // Fallback for browsers without container queries
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
    gap: 12px;
    margin: 0;
  }

  &__tile {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    padding: 16px 18px;
    border: 1px solid $st-border;
    border-radius: 12px;
    background: #fff;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 400;
    color: $st-text-secondary;
  }

  &__help {
    font-size: 12px;
    color: $st-muted;
    cursor: help;

    &:focus-visible {
      outline: 2px solid $st-accent;
      outline-offset: 2px;
      border-radius: 50%;
    }
  }

  // Narrow tiles wrap between units, never inside a number
  &__value {
    margin: 0;
    font-variant-numeric: tabular-nums;
    word-break: keep-all;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.3px;
    color: $st-text;

    &.is-empty {
      font-weight: 500;
      color: $st-muted;
    }
  }

  &__unit {
    margin: 0 4px 0 2px;
    font-size: 14px;
    font-weight: 500;

    &:last-child {
      margin-right: 0;
    }
  }

  &__rest {
    margin-left: 6px;
    font-size: 14px;
    font-weight: 500;
    color: $st-muted;
  }

  &__sub {
    margin: 0;
    font-size: 12px;
    line-height: 1.45;
    color: $st-muted;
    font-variant-numeric: tabular-nums;
    word-break: keep-all;
  }

  // Matches one value line (24px x 1.3)
  &__skeleton {
    display: block;
    height: 31px;
    padding: 3px 0;
  }
}

@container (min-width: 1000px) {
  .usage-tiles__grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@container (min-width: 520px) and (max-width: 999.98px) {
  .usage-tiles__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@container (max-width: 519.98px) {
  .usage-tiles__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
